import { useState } from "react";
import { istBetrieb } from "./konsole-nav";
import { Link, useParams } from "react-router";
import { api } from "../../data";
import { PillView, useAction } from "../../components/ui";
import { money } from "../../lib/format";
import { changePlan, endGrant, extendTrial, type AgenturData } from "./data";
import { useAgentur, useDemoMarks } from "./hooks";
import { DemoGebucht } from "./DemoTermine";
import { Lebenszyklus, Zustellung } from "./Betriebsdaten";
import { AboSteuerung, Anbieterwechsel, ChefSetzen, NurLesenStattSperre } from "./Verwaltung";
import { VerbundSteuerung } from "./Verbund";
import { EnterpriseGrenzen, useEnterpriseStand } from "./Enterprise";
import { mitEnterpriseGrenzen, tarifZusatz } from "./enterprise-model";
import {
  billingEventLabel, billingOutcomePill, countLabel, grantState, intervalLabel, lastInvoiceText, laufzeitAbgelaufen, limitsOf, moduleStates, orgStatusPill, planPreview,
  protocolEntries, signedMoney, splitReason, subscriptionLabel, trialDaysLeft, trialLabel, usagePill, usageRows, utcDate, utcDateTime,
  type OrgOverviewRow, type PlanPreview,
} from "./model";
import { DemoHinweis, Fehler, Fortschritt, Laden, MaybePill } from "./parts";
import { useKonsolenRolle } from "./rolle";
import { KundenakteVertrieb } from "./Kundenakte";
import { DemoMarkierung } from "./DemoKonto";
import "./betrieb.css";

/**
 * Agentur-Detail (17_betreiber/agentur-detail.html): Mandant, Abrechnung, Funktionsschalter, Support, dazu Tarifnutzung und Stripe-Ereignisse.
 * Vertrieb-Konsole (Inhaber 02.11.2026): zugleich die Kundenakte. Der Vertrieb sieht Mandant, Abrechnung, Module und Nutzung und
 * verlängert die Testphase um höchstens 14 Tage; Tarif ändern, Abo, Sperren, Support-Zugang, Admin setzen, Lebenszyklus,
 * Zustellung, Verbund und Stripe-Ereignisse bleiben beim Admin (die Datenbank liefert sie dem Vertrieb ohnehin nicht).
 */
export function AgenturDetail() {
  const { orgId = "" } = useParams();
  const rolle = useKonsolenRolle();
  const q = useAgentur(orgId, rolle);
  if (q.error) return <><title>Agentur · Plattform · CreatorOffice</title><h1>Agentur</h1><Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <><title>Agentur · Plattform · CreatorOffice</title><h1>Agentur</h1><Laden /></>;
  if (!q.data.row) {
    return (
      <>
        <title>Agentur · Plattform · CreatorOffice</title>
        <h1>Agentur nicht gefunden</h1>
        <div className="panel stack tk-empty">
          <p>Diese Agentur gibt es nicht (mehr). Vielleicht wurde sie nach Ablauf der Aufbewahrung endgültig gelöscht.</p>
          <Link className="btn" to="/intern/agenturen">Zur Agenturenliste</Link>
        </div>
      </>
    );
  }
  return <Detail key={orgId} data={q.data} row={q.data.row} admin={istBetrieb(rolle)} />;
}

function Detail({ data, row, admin }: { data: AgenturData; row: OrgOverviewRow; admin: boolean }) {
  const [nowMs] = useState(() => Date.now());
  const [target, setTarget] = useState(row.plan_key ?? data.plans[0]?.key ?? "");
  const [preview, setPreview] = useState<{ key: string; p: PlanPreview } | null>(null);
  const { busy, error, run } = useAction();
  const demo = useDemoMarks().data?.find((m) => m.org_id === row.org_id);
  // 0541: im Tarif Enterprise gelten die Grenzen des Verbunds (Hauptagentur), ggf. mit eigenen Werten aus dem Vertrag
  const enterprise = useEnterpriseStand(row.org_id, admin);
  const limits = mitEnterpriseGrenzen(limitsOf(row.plan_key, data.entitlements), enterprise.data);
  const usage = usageRows(row, limits);
  const modules = moduleStates(limits);
  const days = trialDaysLeft(row, nowMs);
  const own = data.grants.map((g) => ({ g, state: grantState(g, nowMs) }));
  const active = own.find((x) => x.state === "active")?.g ?? null;
  const protocol = protocolEntries(data.grants, data.log, nowMs).slice(0, 5);
  const targetPlan = data.plans.find((p) => p.key === target) ?? null;
  const stripeLinked = !!data.subscription?.stripe_subscription_id;
  const price = row.billing_interval === "year" ? row.price_year_cents : row.price_month_cents;

  function prepare() {
    if (targetPlan) setPreview({ key: targetPlan.key, p: planPreview(row, targetPlan, data.entitlements) });
  }
  async function apply() {
    if (!preview) return;
    const done = await run("tarif", () => changePlan(api, row.org_id, preview.key, data.subscription), `Tarif auf ${targetPlan?.name ?? preview.key} umgestellt.`);
    if (done) setPreview(null);
  }

  return (
    <>
      <title>{`${row.name} · Plattform · CreatorOffice`}</title>
      <h1>{row.name}</h1>
      {/* Vertrieb-Konsole (Inhaber 02.11.2026): Kundenakte mit Vertrieb, Kontakt, Herkunft und Signalen für Admin und Vertrieb */}
      <KundenakteVertrieb orgId={row.org_id} />
      <div className="grid">
        <section className="panel stack">
          <h2>Mandant</h2>
          <p>
            ID: {row.org_id}<br />
            Kürzel: {row.slug}<br />
            Admin: {row.chef_emails.length ? row.chef_emails.join(", ") : "–"}<br />
            Erstellt: {utcDate(row.created_at)}<br />
            Letzte Aktivität: {row.last_activity_at ? utcDateTime(row.last_activity_at) : "noch keine erfasst"}
          </p>
          <div className="row">
            <PillView pill={orgStatusPill(row.status)} />
            <DemoGebucht mark={demo} />
            {days !== null && <span>Testphase bis {utcDate(row.trial_ends_at)} · {trialLabel(days)}</span>}
          </div>
          {admin && <>
          <label>
            Tarif
            <select id="tarif" value={target} onChange={(e) => { setTarget(e.target.value); setPreview(null); }}>
              {data.plans.map((p) => <option key={p.key} value={p.key}>{p.name}{p.key === row.plan_key ? " (aktuell)" : ""}{tarifZusatz(p)}</option>)}
            </select>
          </label>
          <button className="" onClick={prepare} disabled={!targetPlan}>Tarifänderung vorbereiten</button>
          {preview && (
            <div className="stack" aria-live="polite">
              <TarifVorschau p={preview.p} name={data.plans.find((x) => x.key === preview.key)?.name ?? preview.key} nextBilling={row.current_period_end} />
              {!preview.p.same && (
                stripeLinked ? (
                  <p id="tarif-stripe">Dieses Abo führt Stripe. Bitte den Tarif in Stripe umstellen; der Webhook übernimmt Tarif und Status. Hier wird nichts geändert.</p>
                ) : (
                  <>
                    <p id="tarif-hinweis">Das Abo läuft noch nicht über Stripe. Die Umstellung gilt sofort, steht im Protokoll der Agentur und ändert keine Rechnung.</p>
                    <div className="row">
                      <button className="primary" onClick={() => void apply()} disabled={!!busy} aria-busy={busy === "tarif"} aria-describedby="tarif-hinweis">
                        {busy === "tarif" ? "Wird umgestellt …" : "Tarif umstellen"}
                      </button>
                      <button className="" onClick={() => setPreview(null)}>Verwerfen</button>
                    </div>
                  </>
                )
              )}
              {error && <p className="tk-error" role="alert">{error}</p>}
            </div>
          )}
          </>}
        </section>

        <section className="panel stack">
          <h2>Abrechnung</h2>
          <div className="metric num">{price === null ? "–" : money(price)}</div>
          <p>
            {row.plan_name ? `${row.plan_name} · ` : ""}{intervalLabel(row.billing_interval)} · netto · {laufzeitAbgelaufen(row.current_period_end, nowMs) ? <strong className="tk-error">Laufzeit abgelaufen am {utcDate(row.current_period_end)}</strong> : <>Nächste Abrechnung {utcDate(row.current_period_end)}</>}<br />
            Abo: {subscriptionLabel(row.subscription_status)}{admin ? (stripeLinked ? " · über Stripe" : " · noch ohne Stripe") : ""}{admin && <><br />
            Letzte Rechnung: {lastInvoiceText(data.events)}</>}
          </p>
          {admin && <a className="btn " href="#abrechnungsereignisse">Abrechnungsdetails</a>}
        </section>

        <section className="panel stack">
          <h2>Module</h2>
          <ul className="tk-module-liste" aria-label="Module im Tarif">
            {modules.map((m) => (
              <li key={m.key}>{m.on ? <span className="pill success"><span className="pill-zeichen">✓</span> {m.label}</span> : <span className="pill"><span className="pill-zeichen">○</span> {m.label}</span>} <small>{m.on ? "enthalten" : "nicht enthalten"}</small></li>
            ))}
          </ul>
          <small>Die Module folgen dem Tarif{row.plan_name ? ` ${row.plan_name}` : ""}; geändert wird über den Tarif.</small>
        </section>

        {admin ? (
          <>
            <AboSteuerung row={row} data={data} />
            <SupportPanel row={row} active={active} protocol={protocol} />
            <Lebenszyklus orgId={row.org_id} />
            <Zustellung orgId={row.org_id} />
            <ChefSetzen row={row} />
            <NurLesenStattSperre row={row} />
            <Anbieterwechsel row={row} />
            <VerbundSteuerung row={row} />
            <EnterpriseGrenzen orgId={row.org_id} />
            {/* 0461: Demo-Konto für den Vertrieb (nur Agenturen) */}
            {row.status !== "cancelled" && <DemoMarkierung orgId={row.org_id} name={row.name} />}
          </>
        ) : <TestphaseVertrieb row={row} />}
      </div>

      <section className="panel stack" aria-labelledby="nutzung-titel">
        <h2 id="nutzung-titel">Nutzung gegen Tarifgrenzen</h2>
        {usage.map((u) => (
          <div key={u.key} className="tk-usage">
            <div className="row between">
              <span>{u.label}</span>
              <span><span className="num">{u.usedLabel}</span> von {u.maxLabel} <MaybePill pill={usagePill(u)} /></span>
            </div>
            {u.pct !== null && <Fortschritt pct={u.pct} label={`${u.label}: ${u.usedLabel} von ${u.maxLabel}`} />}
          </div>
        ))}
        <p className="source-note">
          Gezählt wie die Tarifgrenzen der Datenbank: Creator ohne archivierte, aktive Team-Plätze, im laufenden Monat (UTC) versendete Verträge,
          Speicher der nicht gelöschten Anhänge. Grenzen greifen beim Anlegen und beim Versand; Bestehendes bleibt bei einem kleineren Tarif erhalten.
        </p>
      </section>

      {admin && <section className="panel stack" id="abrechnungsereignisse" aria-labelledby="ereignisse-titel">
        <h2 id="ereignisse-titel">Abrechnungsereignisse</h2>
        <p>Ereignisse von Stripe, jedes genau einmal verarbeitet. Gezeigt werden Art, Eingang und Ergebnis; Beträge und Belege stehen in Stripe.</p>
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Abrechnungsereignisse von Stripe</caption>
            <thead>
              <tr>
                <th scope="col">Eingang</th>
                <th scope="col">Ereignis</th>
                <th scope="col">Ergebnis</th>
                <th scope="col">Stripe-Kennung</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.stripe_event_id}>
                  <td>{utcDateTime(e.received_at)}</td>
                  <td>{billingEventLabel(e.type)}<small>{e.type}</small></td>
                  <td><PillView pill={billingOutcomePill(e.outcome)} /></td>
                  <td><code>{e.stripe_event_id}</code></td>
                </tr>
              ))}
              {data.events.length === 0 && <tr><td colSpan={4}>Noch keine Abrechnungsereignisse von Stripe für diese Agentur.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>}
      <p className="source-note">Stand der Kennzahlen: {utcDateTime(new Date(nowMs).toISOString())} · {countLabel(row.talents)} Creator · {countLabel(row.seats)} Team-Plätze</p>
      <DemoHinweis />
    </>
  );
}

/**
 * Vertrieb-Konsole (Inhaber 02.11.2026): Testphase verlängern durch den Vertrieb, je Konto zusammen höchstens 14 Tage. Über
 * „konto-dienste“, damit ein Stripe-Probeabo dasselbe Ende bekommt; die Datenbank prüft Rolle, Obergrenze und Stand.
 */
function TestphaseVertrieb({ row }: { row: OrgOverviewRow }) {
  const { busy, error, run } = useAction();
  const [tage, setTage] = useState("7");
  const [notiz, setNotiz] = useState("");
  const testphase = row.status === "trial" || (row.status === "suspended" && !!row.trial_ends_at);
  const n = Math.trunc(Number(tage));
  return (
    <section className="panel stack" aria-labelledby="test-vertrieb-titel">
      <h2 id="test-vertrieb-titel">Testphase verlängern</h2>
      {!testphase ? <p className="muted">Dieses Konto ist nicht in der Testphase.</p> : (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void run("verlaengern", () => extendTrial(api, row.org_id, n, notiz || null, 14), `Testphase um ${n} ${n === 1 ? "Tag" : "Tage"} verlängert.`); }}>
          <p className="muted">Der Vertrieb verlängert je Konto zusammen höchstens 14 Tage. Mehr, Testzugang, Tarif und Sperren nur über einen Admin; einen Rabatt schlägst du in der Kundenakte vor.</p>
          <label>Tage
            <input type="number" inputMode="numeric" min={1} max={14} value={tage} onChange={(e) => setTage(e.target.value)} />
          </label>
          <label>Grund (für das Protokoll)
            <input value={notiz} maxLength={500} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Wunsch im Demo-Gespräch" />
          </label>
          <div className="row"><button type="submit" className="primary" disabled={!!busy || !(n >= 1 && n <= 14)} aria-busy={busy === "verlaengern"}>{busy === "verlaengern" ? "Wird verlängert …" : "Verlängern"}</button></div>
          {error && <p className="tk-error" role="alert">{error}</p>}
        </form>
      )}
    </section>
  );
}

function TarifVorschau({ p, name, nextBilling }: { p: PlanPreview; name: string; nextBilling: string | null }) {
  const per = p.interval === "year" ? "jährlich" : "monatlich";
  if (p.same) return <p>{name} ist der aktuelle Tarif. Es gibt nichts umzustellen.</p>;
  return (
    <div className="stack">
      <p>
        <strong>Vorschau: Wechsel auf {name}</strong><br />
        Preis {per}: {p.priceFrom === null ? "bisher ohne Tarif" : money(p.priceFrom)} → {money(p.priceTo)}{p.diff === null ? "" : ` (${signedMoney(p.diff)})`}, netto<br />
        Nächste Abrechnung laut Abo: {utcDate(nextBilling)}
      </p>
      <div className="tablewrap">
        <table>
          <caption className="sr-only">Grenzen bisher und im neuen Tarif</caption>
          <thead>
            <tr>
              <th scope="col">Grenze</th>
              <th scope="col" className="money">Genutzt</th>
              <th scope="col" className="money">Bisher</th>
              <th scope="col" className="money">Neu</th>
            </tr>
          </thead>
          <tbody>
            {p.limits.map((l) => (
              <tr key={l.label}>
                <td>{l.label}</td>
                <td><span className="num">{l.used}</span></td>
                <td><span className="num">{l.from}</span></td>
                <td><span className="num">{l.to}</span>{l.over && <small><span className="pill error"><span className="pill-zeichen">!</span> Über der Grenze</span></small>}</td>
              </tr>
            ))}
            {p.modules.map((m) => (
              <tr key={m.label}>
                <td>{m.label}</td>
                <td>–</td>
                <td>{m.from ? "enthalten" : "nicht enthalten"}</td>
                <td>{m.to ? "enthalten" : "nicht enthalten"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {p.warnings.length > 0 && (
        <div className="notice" role="status">
          {p.warnings.map((w) => <p key={w}>{w}</p>)}
        </div>
      )}
    </div>
  );
}

function SupportPanel({ row, active, protocol }: {
  row: OrgOverviewRow;
  active: AgenturData["grants"][number] | null;
  protocol: ReturnType<typeof protocolEntries>;
}) {
  const { busy, error, run } = useAction();
  const ticket = active ? splitReason(active.reason).ticket : null;
  return (
    <section className="panel stack">
      <h2>Support</h2>
      {active ? (
        <>
          <p>
            Support-Zugang aktiv bis {utcDateTime(active.expires_at)} · nur Lesen{ticket ? ` · Ticket ${ticket}` : ""}.<br />
            Der Admin sieht den Zugang in seinen Team-Einstellungen und kann ihn beenden.
          </p>
          <div className="row">
            <Link className="btn " to={`/intern/support?agentur=${row.org_id}`}>Zum Support-Zugang</Link>
            <button className="danger" disabled={!!busy} aria-busy={busy === "ende"} onClick={() => void run("ende", () => endGrant(api, active.id), "Support-Zugang beendet.")}>
              Zugang beenden
            </button>
          </div>
        </>
      ) : (
        <>
          <p>Kein aktiver Support-Zugang.</p>
          <Link className="btn " to={`/intern/support?agentur=${row.org_id}`}>Support-Zugang öffnen</Link>
        </>
      )}
      {protocol.length > 0 && (
        <ol>
          {protocol.map((p) => <li key={p.key}>{utcDateTime(p.at)} · {p.text}</li>)}
        </ol>
      )}
      {error && <p className="tk-error" role="alert">{error}</p>}
    </section>
  );
}
